import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent, DwComponentType, DwExpressionService, DwFormContextMode, DwMdFormContextInfo, DwMetaDataFormApi, DwMetaDataFormStateService,
  DwMetaDataService, DwMetaDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken,
  DwLookupSearchType, DwOrmDataService, DwOrmDataServiceToken, DwQueryHelper, DwQueryFilter, DwOperator, DwFilterType,
  isAfterDate,
  DwRoutingService
}
  from '@devwareapps/devware-cap';
import { AppMetaDataItemNames, CourseLessonDocumentLinkReferenceEntity, CourseLessonEntity, CourseLessonProgressEntity, CourseLessonSessionEntity, DocumentGroupAllItems, DocumentGroupEntity, DocumentTypeAllItems, LessonTypeAllItems, QuizResultEntity, ReferenceStatusAllItems, VideoEntity } from '../../../../meta-data/app-meta-data.service';
import { CourseRepositoryService } from '../../services/course-repository.service';
import { CourseNavigationService } from '../../services/course-navigation.service';
import { LessonNavigationInfo } from '../../models/lesson-navigation-info.model';
import { LessonProgressHierarchy } from '../../models/lesson-progress-hierarchy.model';
import { DateTimeUtilService } from '../../../shared/util/date-time-util.service';
import { VideoEvent } from '../../../videos/models/video-event.model';
import { VideoEventType } from '../../../videos/models/video-event-type.enum';
import { QuizProgressEvent } from '../../../quizzes/models/quiz-progress-event.model';
import { LessonFlagUtilService } from '../../services/lesson-flag-util.service';
import { QuizHomeView } from '../../../quizzes/models/quiz-home-view.enum';
import { DocumentProgressEvent } from '../../../documents/models/document-progress-event.model';
import { LessonProgressItem } from '../../models/course-progress-hierarchy.model';
import { finalize, takeUntil } from 'rxjs/operators';
import { forkJoin, Subject } from 'rxjs';
import { DocumentsManagerService } from '../../../documents/services/documents-manager.service';
import { DocumentsRepositoryService } from '../../../documents/services/documents-repository.service';
import { DocumentCardData } from '../../../documents/models/document-card-data.model';
import { StructuredDocumentSectionReferenceModel } from '../../../documents/structured-content/models/structured-document-viewer.model';
import { LessonVideoPlayback } from '../../../videos/models/video-playback.model';
import { ModuleVideoPlaybackService } from '../../../videos/services/module-video-playback.service';

 
@DwComponent({
  key: 'LessonDetailPage',
  name: 'Lesson Detail Page',
  componentType: DwComponentType.pageComponent,
  isGlobal: true
})
@Component({
  selector: 'app-lesson-detail-view',
  templateUrl: './lesson-detail-view.component.html',
  styleUrls: ['./lesson-detail-view.component.scss']
})
export class LessonDetailViewComponent implements OnInit, OnDestroy {
  lesson: CourseLessonEntity;
  video: VideoEntity;
  //formApi: DwMetaDataFormApi;
  lessonNavigationInfo: LessonNavigationInfo;
  progress: LessonProgressHierarchy;
  videoPlayback: LessonVideoPlayback = null;

  videoLessonType = LessonTypeAllItems.Video;
  quizLessonType = LessonTypeAllItems.Quiz;
  documentLessonType = LessonTypeAllItems.ProTools;
  currentUpdateTimeSeconds: number;
  currentTimeToAddSeconds: number;
  pageSessionStart: string;
  lastVideoStart: string;
  private lastVideoProgressSaveDateTime: string;
  private lastSavedFurthestVideoTime: number = 0;
  private pendingVideoProgressUpdate: { furtherestVideoTime: number; percentComplete: number; event?: VideoEvent } | null = null;
  private currentLessonSessionKey: string;
  private videoSessionStarted: boolean = false;
  private videoPlaybackProgressObserved: boolean = false;
  private videoCompletionSaveStarted: boolean = false;
  private videoCompletionSaved: boolean = false;
  private readonly videoProgressAutosaveIntervalSeconds = 30;
  private readonly videoProgressAutosaveDeltaSeconds = 15;
  private readonly maxVideoPlaybackDeltaSeconds = 15;
  private readonly videoSessionInactivitySplitSeconds = 90;
  private observedVideoPlaybackSeconds = 0;
  private lastObservedVideoTimeSeconds: number = null;
  private lastObservedPlaybackDateTime: string = null;
  videoWidth: string;
  flagEnabled: boolean = false;
  flagSubmitted: boolean;
  lessonLocked: boolean = false;

  hideNavigation: boolean = true;

  // Lesson-view redesign: neighbour lessons (for the redesigned Previous /
  // Up-next footer cards) and a derived "has description" flag used to gate
  // the empty-state placeholder in the Aviator Intel overview.
  prevLessonItem: LessonProgressItem | null = null;
  nextLessonItem: LessonProgressItem | null = null;
  hasLessonDescription: boolean = false;

  // ──────────────────────────────────────────────────────────────────
  // MOCKUP DATA — placeholder content rendered on every lesson so the
  // designer can see the full layout (rich overview, Learning Objectives
  // card, multi-resource list) without real content authored in the LMS.
  // Delete this block and the `useMockLessonData` bindings in the template
  // once real fields (objectives collection, multi-document relation) are
  // wired up on CourseLessonEntity.
  // ──────────────────────────────────────────────────────────────────
  useMockLessonData: boolean = false;

  mockResources: { name: string; type: string; size: string }[] = [
    { name: 'Stability — Lesson Notes.pdf', type: 'PDF', size: '1.2 MB' },
    { name: 'Three Axes Diagram.pdf', type: 'PDF', size: '640 KB' },
    { name: 'FAA PHAK Ch. 5 — Aerodynamics.pdf', type: 'PDF', size: '4.8 MB' },
    { name: 'CG & Stability Quick Reference.pdf', type: 'CHART', size: '320 KB' },
  ];
  documentGroup: DocumentGroupEntity;
  allDocumentCards: DocumentCardData[];
  currentDocumentCards: DocumentCardData[];

  // Companion PDF — when a student clicks a PDF resource on a video
  // lesson, a fixed right-rail "study companion" docks into the page so
  // they can read along while the video plays. The companion embeds
  // <app-document-viewer> directly so students get the full toolset
  // (highlight, search, download, fullscreen) for free; this component
  // just owns the open / close / route-fallback state.
  activeCompanionCard: DocumentCardData | null = null;

  // Two-phase close so the companion can play an exit animation before
  // unmounting. closeCompanion() flips this flag, the template applies
  // the .lesson-detail-view--companion-closing modifier (which triggers
  // the exit animation + lv-main margin reflow), and a setTimeout
  // matching --duration-normal removes the element from the DOM. Users
  // with prefers-reduced-motion skip the wait entirely.
  companionClosing = false;
  private static readonly COMPANION_EXIT_MS = 200; // matches --duration-normal

  constructor(
    // dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    // @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    // @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    // @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
    // private dwFormStateService: DwMetaDataFormStateService,
    private dwRoutingService: DwRoutingService,
    private courseRepositoryService: CourseRepositoryService,
    private courseNavigationService: CourseNavigationService,
    private dateTimeUtilService: DateTimeUtilService,
    private lessonFlagUtilService: LessonFlagUtilService,
    private moduleVideoPlaybackService: ModuleVideoPlaybackService,
    private documentRepositoryService: DocumentsRepositoryService,
    private documentManagerService: DocumentsManagerService
  ) {
    //super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    //this.formApi = this.dwFormStateService.state.formApi;
  }
  ngOnDestroy(): void {
    this.flushVideoProgress(true);
    this.moduleVideoPlaybackService.clearActiveModule();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.setupLesson();

    // DevWare's getRoutesChanges fires for every URL change including
    // in-app metadata-driven transitions that don't always emit a standard
    // NavigationEnd. The course-navigation sidebar uses the same pattern.
    this.dwRoutingService.getRoutesChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.setupLesson());
  }

  quizViewChanged(view: QuizHomeView) {
    switch (view) {
      case QuizHomeView.quiz:
      case QuizHomeView.quizAnswers:
        this.hideNavigation = true;
        break;
      default:
        this.hideNavigation = false;
        break;
    }
  }

  protected setupLesson(): void {
    this.flushVideoProgress(true);
    this.videoPlayback = null;
    this.moduleVideoPlaybackService.clearActiveModule();

    // Reset the companion when the lesson route changes — the previous
    // lesson's PDF doesn't belong on the new lesson's stage.
    this.closeCompanion();

    const courseParameters = this.courseNavigationService.getCurrentCourseParameters();

    this.courseRepositoryService.getLessonProgress(courseParameters)
      .subscribe(lessonProgress => {
        this.progress = lessonProgress;

        this.lesson = this.progress.lesson.lesson;

        if (this.lesson?.LessonTypeId === LessonTypeAllItems.Video) {
          const studentCourseId = this.progress?.course?.studentCourse?.StudentCourseId;
          const courseModuleId = this.progress?.module?.module?.CourseModuleId;
          const courseLessonId = this.lesson?.CourseLessonId;

          if (studentCourseId && courseModuleId && courseLessonId) {
            this.moduleVideoPlaybackService.setActiveModule(studentCourseId, courseModuleId);
            this.moduleVideoPlaybackService
              .getLessonPlayback(studentCourseId, courseModuleId, courseLessonId)
              .pipe(takeUntil(this.destroy$))
              .subscribe(playback => {
                if (this.lesson?.CourseLessonId === playback?.LessonId) {
                  this.videoPlayback = {
                    ...playback,
                    LessonCompleted: playback.LessonCompleted === true
                      || this.progress?.lesson?.lessonProgress?.LessonComplete === true
                  };
                }
              });
          }
        }

        let allowLessonLock = true;
        switch (this.lesson?.LessonTypeId) {
          case LessonTypeAllItems.Video:
            allowLessonLock = false;
        }

        const studentCourse = lessonProgress?.course?.studentCourse;
        const writtenUploadedAndPassed = studentCourse?.AcsTestUploaded === true && studentCourse?.AcsTestPassed === true;
        const isSketchfabLesson = lessonProgress?.lesson?.lesson?.LessonTypeId === LessonTypeAllItems.ProTools
          && lessonProgress?.lesson?.lesson?.DocumentItem?.DocumentTypeId === DocumentTypeAllItems.SketchFab3D;
        this.lessonLocked = allowLessonLock && !(isSketchfabLesson && writtenUploadedAndPassed)
          && isAfterDate(studentCourse?.CourseEndorsementStudent?.ExpirationDateTime);

        this.setupLessonDetails()

        this.setupLessonSession();

      //  this.computeLessonViewState();

        this.setupRelatedDocuments();

        this.hideNavigation = false;
      });

    this.courseNavigationService.getLessonNavigationInfo(courseParameters)
      .subscribe(lessonNavigationInfo => {
        this.lessonNavigationInfo = lessonNavigationInfo;
      });
  }

  
  documentCardClick(card: DocumentCardData) {
    // PDFs and FAR/AIM references open inline alongside the video. Other
    // resources keep the legacy full-page route since they need their own chrome.
    const isPdf = card?.documentItem?.DocumentTypeId === DocumentTypeAllItems.PDFDocument;
    const isStructuredReference = card?.documentItem?.DocumentTypeId === DocumentTypeAllItems.FARAIM;
    const isVideoLesson = this.lesson?.LessonTypeId === LessonTypeAllItems.Video;

    if ((isPdf || isStructuredReference) && isVideoLesson) {
      this.openCompanion(card);
      return;
    }

    this.documentManagerService.navigateToDocument(card, this.allDocumentCards);
  }

  openCompanion(card: DocumentCardData) {
    const isStructuredReference = card?.documentItem?.DocumentTypeId === DocumentTypeAllItems.FARAIM;
    if (!card?.documentItem?.MediaId && !isStructuredReference) {
      // No media to render — fall through to the standalone viewer route
      // which will surface its own "missing media" state.
      this.documentManagerService.navigateToDocument(card, this.allDocumentCards);
      return;
    }

    // Toggle off if the same card is clicked again
    if (this.activeCompanionCard?.uniqueKey === card.uniqueKey) {
      this.closeCompanion();
      return;
    }

    // The embedded <app-document-viewer> resolves the media URL itself
    // off the documentItem.MediaId — we just hand it the card.
    this.activeCompanionCard = card;
  }

  closeCompanion() {
    if (!this.activeCompanionCard) return;
    if (this.companionClosing) return;

    // Reduced-motion users skip the animation entirely — instant close.
    const reduceMotion = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      this.activeCompanionCard = null;
      return;
    }

    // Phase 1: keep the card mounted, flip the flag so CSS plays the
    // exit animation (and the lesson canvas reflows back).
    this.companionClosing = true;

    // Phase 2: unmount once the animation completes. Using setTimeout
    // (rather than `animationend`) is simpler and survives the case
    // where the user hops to a new lesson mid-close — see setupLesson
    // which also calls closeCompanion().
    setTimeout(() => {
      this.activeCompanionCard = null;
      this.companionClosing = false;
    }, LessonDetailViewComponent.COMPANION_EXIT_MS);
  }

  setupRelatedDocuments() {
    const lessonDocumentLinks = (this.lesson.CourseLessonDocumentLink || [])
      .filter(link => link.CourseLessonDocumentLinkStatusId === ReferenceStatusAllItems.Active);
    const documentIds = lessonDocumentLinks.map(link => link.DocumentItemId);
    const firstPageByDocumentId = new Map<number, number>();
    const pageNumbersByDocumentId = new Map<number, string[]>();
    const structuredReferencesByDocumentId = new Map<number, StructuredDocumentSectionReferenceModel[]>();

    lessonDocumentLinks.forEach(link => {
      if (link?.DocumentItemId && link.FirstPageNumber && !firstPageByDocumentId.has(link.DocumentItemId)) {
        firstPageByDocumentId.set(link.DocumentItemId, link.FirstPageNumber);
      }

      if (link?.DocumentItemId && link.PageNumbers) {
        const existingPageNumbers = pageNumbersByDocumentId.get(link.DocumentItemId) || [];
        pageNumbersByDocumentId.set(link.DocumentItemId, [...existingPageNumbers, link.PageNumbers]);
      }

      if (link?.DocumentItemId && link.CourseLessonDocumentLinkReference?.length) {
        const existingReferences = structuredReferencesByDocumentId.get(link.DocumentItemId) || [];
        structuredReferencesByDocumentId.set(link.DocumentItemId, [
          ...existingReferences,
          ...this.buildStructuredDocumentReferences(link.DocumentItemId, link.CourseLessonDocumentLinkReference)
        ]);
      }
    });

    forkJoin([
      this.documentRepositoryService.getDocumentGroup(DocumentGroupAllItems.Documents),
      this.documentRepositoryService.getDocumentGroup(DocumentGroupAllItems.FARAIM)
    ])
      .subscribe(documentGroups => {
        this.documentGroup = documentGroups[0];

        const cardsByDocumentItemId = new Map<number, DocumentCardData>();
        documentGroups.forEach(documentGroup => {
          this.documentManagerService.buildAllDocumentCards(documentGroup).forEach(card => {
            const documentItemId = card.documentItem?.DocumentItemId;
            if (documentItemId && !cardsByDocumentItemId.has(documentItemId)) {
              cardsByDocumentItemId.set(documentItemId, card);
            }
          });
        });

        this.allDocumentCards = Array.from(cardsByDocumentItemId.values());

        this.currentDocumentCards = this.allDocumentCards
          .filter(card => documentIds.includes(card.documentItem?.DocumentItemId || -1))
          .map(card => ({
            ...card,
            initialPageNumber: firstPageByDocumentId.get(card.documentItem?.DocumentItemId),
            pageNumbers: pageNumbersByDocumentId.get(card.documentItem?.DocumentItemId)?.join(','),
            structuredDocumentReferences: structuredReferencesByDocumentId.get(card.documentItem?.DocumentItemId) || []
          }));
      });
  }

  private buildStructuredDocumentReferences(
    documentItemId: number,
    linkReferences: CourseLessonDocumentLinkReferenceEntity[]
  ): StructuredDocumentSectionReferenceModel[] {
    const seenSectionIds = new Set<number>();
    const references: StructuredDocumentSectionReferenceModel[] = [];

    linkReferences
      .slice()
      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
      .forEach(reference => {
        const documentSectionId = reference.DocumentSectionId;
        if (!documentSectionId || seenSectionIds.has(documentSectionId)) {
          return;
        }

        const section = reference.DocumentSection;
        seenSectionIds.add(documentSectionId);
        references.push({
          documentItemId,
          documentVersionId: reference.DocumentVersionId,
          documentSectionId,
          label: reference.ReferenceLocatorText || section?.Label || section?.SectionKey || reference.DocumentSectionIdDisplay || '',
          title: section?.Title,
          displayOrder: reference.DisplayOrder
        });
      });

    return references;
  }

  flagLesson() {
    if (this.flagSubmitted) {
      return;
    }

    this.lessonFlagUtilService.showLessonFlagModal(this.progress.course.course.CourseId, this.lesson.CourseLessonId)
      .subscribe(result => {
        if (!result.cancelled) {
          this.flagSubmitted = true;
        }
      });
  }

  setupLessonDetails() {
    switch (this.lesson.LessonTypeId) {
      case LessonTypeAllItems.Video:
        this.setupVideoLesson();
        break;
      case LessonTypeAllItems.Quiz:
        this.setupQuizLesson();
        break;
    }
  }

  videoWidthChanged(width: string) {
    this.videoWidth = width;
  }

  documentWidthChanged(width: string) {
    this.videoWidth = width;
  }

  setupQuizLesson() {

  }

  private setupVideoLesson() {
    
  }

  //  No longer needed since it's included in the lessonNavigationInfo returned from the CourseNavigationService.
  //Walk the course → module → lesson hierarchy, find the current lesson by
  // // CourseLessonId, and grab its neighbours for the redesigned footer cards.
  // // Returning null when there's no previous/next lets the template render the
  // // "This is the first/last lesson." placeholder.
  // private computeLessonViewState() {
  //   const currentId = this.lesson?.CourseLessonId;
  //   const flatLessons: LessonProgressItem[] = (this.progress?.course?.modules || [])
  //     .reduce<LessonProgressItem[]>((acc, m) => acc.concat(m?.lessons || []), []);

  //   const idx = flatLessons.findIndex(l => l?.lesson?.CourseLessonId === currentId);
  //   this.prevLessonItem = idx > 0 ? flatLessons[idx - 1] : null;
  //   this.nextLessonItem = idx >= 0 && idx < flatLessons.length - 1 ? flatLessons[idx + 1] : null;

  //   const desc = this.lesson?.LessonDescription;
  //   this.hasLessonDescription = !!(desc && desc.replace(/<[^>]*>/g, '').trim().length > 0);
  // }

  navigateToPreviousLesson() {
    if (!this.lessonNavigationInfo?.previousLesson) return;
    this.courseNavigationService.navigateToLesson(this.lessonNavigationInfo.previousLesson);
  }

  navigateToNextLesson() {
    if (!this.lessonNavigationInfo?.nextLesson) return;
    if (this.lessonNavigationInfo.isLastLesson) {
      this.courseNavigationService.navigateToModule(
        this.lessonNavigationInfo.course?.CourseId,
        this.lessonNavigationInfo.nextLesson?.moduleId
      );
      return;
    }
    this.courseNavigationService.navigateToLesson(this.lessonNavigationInfo.nextLesson);
  }

  videoEvent(videoEvent: VideoEvent) {

    switch (videoEvent.videoEventType) {
      case VideoEventType.videoStopped:
      case VideoEventType.videoCompleted:
        this.setVideoProgress(videoEvent.furtherestVideoTime, videoEvent.percentComplete, videoEvent);
        break;
      case VideoEventType.videoPaused:
        this.setVideoProgress(videoEvent.furtherestVideoTime, videoEvent.percentComplete, videoEvent);
        break;
      case VideoEventType.videoProgress:
        this.handleVideoProgress(videoEvent);
        break;
      case VideoEventType.videoStarted:
        this.videoSessionStarted = true;
        this.lastVideoStart = this.dateTimeUtilService.getCurrentDateTime();
        if (!this.progress.currentLessonSession.SessionStartDateTime) {
          this.progress.currentLessonSession.SessionStartDateTime = this.lastVideoStart;
        }
    }
  }

  @HostListener('window:pagehide')
  onPageHide() {
    this.flushVideoProgress();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.flushVideoProgress();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.flushVideoProgress();
    }
  }

  documentSessionMaxTimeSeconds = 2 * 60; // 2 minutes

  documentEvent(documentEvent: DocumentProgressEvent) {
    const currentLessonSession = this.progress.currentLessonSession;
    currentLessonSession.LessonCompleted = documentEvent.isComplete;
    currentLessonSession.SessionStartDateTime = this.pageSessionStart;
    currentLessonSession.SessionEndDateTime = this.dateTimeUtilService.getCurrentDateTime();
    this.updateSessionTime();

    if (currentLessonSession.TotalTimeSeconds >= this.documentSessionMaxTimeSeconds) {
      currentLessonSession.TotalTimeSeconds = this.documentSessionMaxTimeSeconds;
    }

    const lessonProgress: Partial<CourseLessonProgressEntity> = {};

    lessonProgress.LastActivityDateTime = currentLessonSession.SessionEndDateTime;

    this.courseRepositoryService.updateLessonProgress(this.progress, lessonProgress, this.currentTimeToAddSeconds, false)
      .subscribe(result => {
        this.progress = result;

        //this.setupLessonSession();
      });
  }

  quizEvent(quizEvent: QuizProgressEvent) {
    const currentLessonSession = this.progress.currentLessonSession;
    currentLessonSession.LessonCompleted = quizEvent.isComplete;
    currentLessonSession.QuizResultId = quizEvent.quizResult?.QuizResultId;

    currentLessonSession.SessionStartDateTime = quizEvent.quizResult?.QuizStartDateTime;
    currentLessonSession.SessionEndDateTime = quizEvent.quizResult?.QuizStopDateTime;

    this.updateSessionTime(quizEvent.quizResult?.TotalTimeSeconds);

    const lessonProgress: Partial<CourseLessonProgressEntity> = {};

    // Don't update this since it will be handled on the server and trigger that a lesson was completed
    // if (quizEvent.isComplete) {
    // lessonProgress.LessonComplete = true;
    //   lessonProgress.PercentComplete = 100;
    // } 

    lessonProgress.BestQuizResultId = quizEvent.bestQuizResultId;
    lessonProgress.LastActivityDateTime = quizEvent.quizResult?.QuizStopDateTime;

    const forceReload = quizEvent.quizResult.ProgressReset;

    this.courseRepositoryService.updateLessonProgress(this.progress, lessonProgress, this.currentTimeToAddSeconds, forceReload)
      .subscribe(result => {
        this.progress = result;

        this.setupLessonSession();
      });
  }

  protected setupLessonSession() {
    this.pageSessionStart = this.dateTimeUtilService.getCurrentDateTime()
    this.lastVideoProgressSaveDateTime = null;
    this.lastSavedFurthestVideoTime = this.progress?.lesson?.lessonProgress?.VideoFurthestTimeSeconds || 0;
    this.pendingVideoProgressUpdate = null;
    this.currentLessonSessionKey = this.getLessonSessionKey();
    this.videoSessionStarted = false;
    this.videoPlaybackProgressObserved = false;
    this.videoCompletionSaveStarted = false;
    this.videoCompletionSaved = false;
    this.observedVideoPlaybackSeconds = 0;
    this.lastObservedVideoTimeSeconds = null;
    this.lastObservedPlaybackDateTime = null;
    this.lastVideoStart = null;
    this.currentTimeToAddSeconds = 0;
    this.updatingProgress = false;

    this.progress.currentLessonSession = {
      SessionStartDateTime: null,
      SessionEndDateTime: null,
      TotalTimeSeconds: 0,

      VideoId: this.lesson?.Video?.VideoId,
    }
  }

  markLessonComplete() {
    this.progress.currentLessonSession.LessonCompleted = true;

    const dateTime = this.dateTimeUtilService.getCurrentDateTime();

    if (!this.progress.currentLessonSession.SessionStartDateTime) {
      this.progress.currentLessonSession.SessionStartDateTime = this.pageSessionStart;
    }

    this.progress.currentLessonSession.SessionEndDateTime = dateTime;

    this.updateSessionTime();

    this.courseRepositoryService.updateLessonProgress(this.progress, { LessonComplete: true, LastActivityDateTime: dateTime }, this.currentTimeToAddSeconds, false)
      .subscribe(result => {
        this.progress = result;
      })
  }

  updatingProgress = false;
  setVideoProgress(furtherestVideoTime: number, percentComplete: number, event?: VideoEvent) {
    furtherestVideoTime = furtherestVideoTime || 0;
    percentComplete = percentComplete || 0;
    const isCompletion = percentComplete >= 100 || event?.videoEventType === VideoEventType.videoCompleted;

    if (isCompletion) {
      percentComplete = 100;
    }

    if (!this.videoSessionStarted && !furtherestVideoTime && percentComplete < 100) {
      return;
    }

    if (this.updatingProgress) {
      const pendingIsCompletion = this.pendingVideoProgressUpdate?.percentComplete >= 100
        || this.pendingVideoProgressUpdate?.event?.videoEventType === VideoEventType.videoCompleted;

      if (pendingIsCompletion && !isCompletion) {
        return;
      }

      if (isCompletion && (this.videoCompletionSaveStarted || this.videoCompletionSaved)) {
        return;
      }

      //console.log('Video Progress - Skipping Update', furtherestVideoTime, percentComplete, event);
      this.pendingVideoProgressUpdate = { furtherestVideoTime, percentComplete, event };
      return;
    }

    if (isCompletion) {
      if (this.videoCompletionSaveStarted || this.videoCompletionSaved) {
        return;
      }

      this.videoCompletionSaveStarted = true;
    }

    const lessonProgress: Partial<CourseLessonProgressEntity> = {};

    this.progress.currentLessonSession.LessonCompleted = (percentComplete >= 100);
    this.progress.currentLessonSession.VideoFurthestTimeSeconds = furtherestVideoTime;

    if (event?.videoEventType !== VideoEventType.videoProgress) {
      this.recordVideoPlaybackProgress(event);
    }

    //console.log('Video Progress - Updating', furtherestVideoTime, percentComplete, event);

    if (!lessonProgress.VideoFurthestTimeSeconds || lessonProgress.VideoFurthestTimeSeconds < furtherestVideoTime) {
      lessonProgress.VideoFurthestTimeSeconds = furtherestVideoTime;
    }
    const dateTime = this.dateTimeUtilService.getCurrentDateTime();

    if (!this.lastVideoStart) {
      this.lastVideoStart = this.progress.currentLessonSession.SessionStartDateTime || dateTime;
    }

    //lessonProgress.LastActivityDateTime = dateTime;
    this.progress.currentLessonSession.SessionEndDateTime = dateTime;

    let previousSeconds = this.progress.currentLessonSession.TotalTimeSeconds || 0;

    let newTotalTimeSeconds = Math.max(previousSeconds, this.observedVideoPlaybackSeconds);

    this.updateSessionTime(newTotalTimeSeconds);

    if (!this.hasVideoProgressChangesToSave(furtherestVideoTime, percentComplete)) {
      return;
    }

    this.updatingProgress = true;
    const saveSessionKey = this.currentLessonSessionKey;

    this.courseRepositoryService.updateLessonProgress(this.progress, lessonProgress, this.currentTimeToAddSeconds)
      .pipe(finalize(() => {
        if (this.isCurrentLessonSession(saveSessionKey)) {
          this.updatingProgress = false;
          this.flushPendingVideoProgressUpdate();
        }
      }))
      .subscribe(result => {
        if (!this.isCurrentLessonSession(saveSessionKey)) {
          return;
        }

        console.log('Video Progress - Update Complete', furtherestVideoTime, percentComplete, result, event);
        this.progress = result;
        this.lastVideoProgressSaveDateTime = this.progress.currentLessonSession?.SessionEndDateTime || dateTime;
        this.lastSavedFurthestVideoTime = this.progress.currentLessonSession?.VideoFurthestTimeSeconds || furtherestVideoTime || this.lastSavedFurthestVideoTime;
        this.lastVideoStart = this.progress.currentLessonSession?.SessionEndDateTime || dateTime;

        if (isCompletion) {
          if (!this.isVideoCompletionSaved(result)) {
            this.videoCompletionSaveStarted = false;
            return;
          }

          this.videoCompletionSaved = true;
          this.videoSessionStarted = false;
          this.videoPlaybackProgressObserved = false;
          this.pendingVideoProgressUpdate = null;
          this.lastVideoStart = null;
          this.observedVideoPlaybackSeconds = 0;
          this.lastObservedVideoTimeSeconds = null;
          this.lastObservedPlaybackDateTime = null;
        }
      }, error => {
        if (isCompletion) {
          this.videoCompletionSaveStarted = false;
        }

        console.warn('Video Progress - Update Failed', furtherestVideoTime, percentComplete, error, event);
      })
  }

  private handleVideoProgress(videoEvent: VideoEvent) {
    if (!this.progress?.currentLessonSession) {
      return;
    }

    const furtherestVideoTime = videoEvent.furtherestVideoTime || 0;
    const videoTime = videoEvent.videoTime || 0;
    const currentFurthest = this.progress.currentLessonSession.VideoFurthestTimeSeconds || 0;

    this.recordVideoPlaybackProgress(videoEvent);

    if (furtherestVideoTime > currentFurthest) {
      this.videoSessionStarted = true;
      this.videoPlaybackProgressObserved = true;
      this.progress.currentLessonSession.VideoFurthestTimeSeconds = furtherestVideoTime;
    }

    if (videoTime > 0) {
      this.videoSessionStarted = true;
      this.videoPlaybackProgressObserved = true;
    }

    if (this.shouldAutosaveVideoProgress(videoEvent)) {
      this.setVideoProgress(furtherestVideoTime, videoEvent.percentComplete, videoEvent);
    }
  }

  private shouldAutosaveVideoProgress(videoEvent: VideoEvent): boolean {
    const furtherestVideoTime = videoEvent.furtherestVideoTime || 0;

    if (videoEvent.percentComplete >= 100) {
      return true;
    }

    const furthestDelta = furtherestVideoTime - (this.lastSavedFurthestVideoTime || 0);
    if (furthestDelta >= this.videoProgressAutosaveDeltaSeconds) {
      return true;
    }

    if (!this.progress?.lesson?.lessonProgress?.CourseLessonProgressId) {
      return false;
    }

    if (!this.lastVideoProgressSaveDateTime) {
      return false;
    }

    const secondsSinceLastSave = this.dateTimeUtilService.calcDurationSeconds(
      this.lastVideoProgressSaveDateTime,
      this.dateTimeUtilService.getCurrentDateTime(),
      0
    );

    return secondsSinceLastSave >= this.videoProgressAutosaveIntervalSeconds;
  }

  private flushVideoProgress(resetSessionAfterFlush: boolean = false) {
    if (!this.progress?.currentLessonSession || this.lesson?.LessonTypeId !== LessonTypeAllItems.Video) {
      return;
    }

    if (!this.videoSessionStarted || this.videoCompletionSaveStarted || this.videoCompletionSaved) {
      return;
    }

    const furtherestVideoTime = this.progress.currentLessonSession.VideoFurthestTimeSeconds
      || this.progress.lesson?.lessonProgress?.VideoFurthestTimeSeconds
      || 0;
    const percentComplete = this.progress.lesson?.lessonProgress?.PercentComplete || 0;

    if (!furtherestVideoTime && !this.lastVideoStart && this.progress.lesson?.lessonProgress?.CourseLessonProgressId) {
      return;
    }

    this.setVideoProgress(furtherestVideoTime, percentComplete);

    if (resetSessionAfterFlush) {
      this.videoSessionStarted = false;
      this.videoPlaybackProgressObserved = false;
      this.pendingVideoProgressUpdate = null;
      this.lastVideoStart = null;
      this.observedVideoPlaybackSeconds = 0;
      this.lastObservedVideoTimeSeconds = null;
      this.lastObservedPlaybackDateTime = null;
      this.currentTimeToAddSeconds = 0;
    }
  }

  private recordVideoPlaybackProgress(videoEvent?: VideoEvent) {
    const currentVideoTime = videoEvent?.videoTime || videoEvent?.furtherestVideoTime || 0;

    if (!currentVideoTime) {
      return;
    }

    const currentDateTime = this.dateTimeUtilService.getCurrentDateTime();

    if (this.shouldSplitVideoSession(currentDateTime)) {
      this.startNewVideoSessionAfterInactivity(currentDateTime, currentVideoTime);
      return;
    }

    if (videoEvent?.userSeeked) {
      this.lastObservedVideoTimeSeconds = currentVideoTime;
      this.lastObservedPlaybackDateTime = currentDateTime;
      return;
    }

    if (this.lastObservedVideoTimeSeconds == null) {
      this.lastObservedVideoTimeSeconds = currentVideoTime;
      this.lastObservedPlaybackDateTime = currentDateTime;
      return;
    }

    const videoDeltaSeconds = Math.floor(currentVideoTime) - Math.floor(this.lastObservedVideoTimeSeconds);
    this.lastObservedVideoTimeSeconds = currentVideoTime;
    this.lastObservedPlaybackDateTime = currentDateTime;

    if (videoDeltaSeconds <= 0) {
      return;
    }

    this.observedVideoPlaybackSeconds += Math.min(videoDeltaSeconds, this.maxVideoPlaybackDeltaSeconds);
  }

  private shouldSplitVideoSession(currentDateTime: string): boolean {
    if (!this.lastObservedPlaybackDateTime || !this.progress?.currentLessonSession?.SessionStartDateTime) {
      return false;
    }

    const inactiveSeconds = this.dateTimeUtilService.calcDurationSeconds(this.lastObservedPlaybackDateTime, currentDateTime, 0);

    return inactiveSeconds >= this.videoSessionInactivitySplitSeconds;
  }

  private startNewVideoSessionAfterInactivity(currentDateTime: string, currentVideoTime: number) {
    this.progress.currentLessonSession = {
      SessionStartDateTime: currentDateTime,
      SessionEndDateTime: null,
      TotalTimeSeconds: 0,
      VideoId: this.lesson?.Video?.VideoId,
      VideoFurthestTimeSeconds: this.progress.lesson?.lessonProgress?.VideoFurthestTimeSeconds || 0
    };

    this.videoSessionStarted = true;
    this.videoPlaybackProgressObserved = false;
    this.videoCompletionSaveStarted = false;
    this.videoCompletionSaved = false;
    this.pendingVideoProgressUpdate = null;
    this.lastVideoStart = currentDateTime;
    this.lastVideoProgressSaveDateTime = null;
    this.lastSavedFurthestVideoTime = this.progress.lesson?.lessonProgress?.VideoFurthestTimeSeconds || 0;
    this.observedVideoPlaybackSeconds = 0;
    this.currentTimeToAddSeconds = 0;
    this.lastObservedVideoTimeSeconds = currentVideoTime || null;
    this.lastObservedPlaybackDateTime = currentDateTime;
  }

  private flushPendingVideoProgressUpdate() {
    const pendingUpdate = this.pendingVideoProgressUpdate;
    this.pendingVideoProgressUpdate = null;

    if (pendingUpdate) {
      this.setVideoProgress(pendingUpdate.furtherestVideoTime, pendingUpdate.percentComplete, pendingUpdate.event);
    }
  }

  private hasVideoProgressChangesToSave(furtherestVideoTime: number, percentComplete: number): boolean {
    if (percentComplete >= 100) {
      return true;
    }

    return this.videoPlaybackProgressObserved && this.currentTimeToAddSeconds > 0;
  }

  private getLessonSessionKey(): string {
    return [
      this.progress?.lessonReference?.courseId,
      this.progress?.lessonReference?.moduleId,
      this.progress?.lessonReference?.lessonId,
      this.lesson?.CourseLessonId
    ].join('|');
  }

  private isCurrentLessonSession(sessionKey: string): boolean {
    return sessionKey === this.currentLessonSessionKey;
  }

  private isVideoCompletionSaved(progress: LessonProgressHierarchy): boolean {
    const savedLessonProgress = progress?.lesson?.lessonProgress;

    return savedLessonProgress?.LessonComplete === true || (savedLessonProgress?.PercentComplete || 0) >= 100;
  }


  private updateSessionTime(newTotalSeconds?: number) {
    let previousSeconds = this.progress.currentLessonSession.TotalTimeSeconds || 0;

    if (newTotalSeconds !== undefined && newTotalSeconds !== null) {
      this.progress.currentLessonSession.TotalTimeSeconds = newTotalSeconds;
    } else {
      this.progress.currentLessonSession.TotalTimeSeconds = this.dateTimeUtilService.calcDurationSeconds(this.progress.currentLessonSession.SessionStartDateTime, this.progress.currentLessonSession.SessionEndDateTime, 0);
    }

    this.currentTimeToAddSeconds = this.progress.currentLessonSession.TotalTimeSeconds - previousSeconds;
  }


  updateLessonProgress() {

  }

}
